import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  viewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NgControl, ReactiveFormsModule } from '@angular/forms';
import { UiWarningIconDirective } from '../../icons/warning-icon.directive';

@Component({
  selector: 'ui-textarea',
  imports: [ReactiveFormsModule, UiWarningIconDirective],
  templateUrl: './textarea.component.html',
  styleUrls: ['../../_styles/index.scss', '../../_styles/primitives/inputs.scss', './textarea.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextareaComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  private readonly injector = inject(Injector);
  private ngControl: NgControl | null = null;

  // Component inputs
  readonly id = crypto.randomUUID();
  readonly label = input.required<string>();
  readonly placeholder = input<string>('');
  readonly labelPosition = input<'top' | 'left' | 'right' | 'hide'>('top');
  readonly resizable = input<'horizontal' | 'vertical' | 'both' | 'none'>('none');
  readonly height = input<number>(3);
  readonly expandWithContent = input<boolean>(true);
  readonly errorMessageId = input<string>(`${this.id}-error`);

  protected readonly disabled = signal(false);
  protected readonly isValid = signal<boolean | undefined>(undefined);
  protected readonly isInvalid = signal<boolean>(false);
  private readonly textareaElement = viewChild<ElementRef<HTMLTextAreaElement>>('textareaElement');

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null, { optional: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    this.ngControl?.statusChanges?.subscribe(status => {
      this.isValid.set(!!(this.ngControl?.control?.valid && this.ngControl?.control?.touched));
      this.isInvalid.set(!!(this.ngControl?.control?.invalid && this.ngControl?.control?.touched));
    });
  }

  // Build aria-describedby IDs for error messages
  get errorMessageIds(): string {
    const errors = this.ngControl?.control?.errors;
    if (!errors) return '';
    return Object.keys(errors)
      .map(errorKey => `${this.errorMessageId()}-${errorKey}`)
      .join(' ');
  }

  // ControlValueAccessor callbacks
  private onChange = (value: string | null) => {};
  private onTouched = () => {};

  // ControlValueAccessor methods
  writeValue(value: string | null): void {
    const element = this.textareaElement()?.nativeElement;
    if (element) {
      element.value = value ?? '';
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Event handlers
  handleOnBlur(): void {
    this.onTouched();
    const target = this.textareaElement()?.nativeElement;
    if (!target) return;
    const value = target.value;
    this.onChange(value ? value : null);
  }

  handleOnInput(): void {
    const target = this.textareaElement()?.nativeElement;
    if (!target) return;
    const value = target.value;
    this.onChange(value ? value : null);
  }
}
